/**
 * SIWE - Sign-In with Ethereum (EIP-4361)
 *
 * Provides secure wallet authentication using the SIWE standard.
 * This proves wallet ownership without any centralized dependencies.
 *
 * Security Features:
 * - Domain validation via explicit allowlist (NOT window.location)
 * - Chain ID validation via explicit allowlist
 * - Session-bound nonces (when fds-id available)
 * - One-time nonce use
 * - 5-minute nonce TTL
 *
 * Optional fds-id Integration:
 * - Server-validated nonces with session binding
 * - Server-side signature verification
 * - Graceful fallback to local operations when unavailable
 *
 * @see https://eips.ethereum.org/EIPS/eip-4361
 */

import {
  getFdsIdClient,
  withFdsIdFallback,
  storeLocalNonce,
  verifyAndConsumeNonce,
  isDegradedModeNotified,
  markDegradedModeNotified,
} from '../fds-id'

// ============================================================
// Security Configuration
// ============================================================

/**
 * Explicit domain allowlist - NOT window.location.host
 * localhost only allowed in development builds
 */
const ALLOWED_DOMAINS: string[] = import.meta.env.DEV
  ? ['fairdrop.xyz', 'staging.fairdrop.xyz', 'localhost:3000', 'localhost:5173']
  : ['fairdrop.xyz', 'staging.fairdrop.xyz']

/**
 * Explicit chain ID allowlist
 * 1: Ethereum Mainnet
 * 100: Gnosis Chain
 * 11155111: Sepolia Testnet
 */
const ALLOWED_CHAIN_IDS: number[] = [1, 100, 11155111]

/**
 * Nonce TTL (5 minutes)
 */
const NONCE_TTL_MS = 5 * 60 * 1000

// ============================================================
// Types
// ============================================================

/**
 * SIWE message parameters
 */
export interface SIWEMessageParams {
  /** Domain requesting the sign-in (e.g., "fairdrop.xyz") */
  domain: string
  /** Ethereum address performing the sign-in */
  address: string
  /** Human-readable statement about the sign-in */
  statement?: string
  /** URI of the resource (e.g., "https://fairdrop.xyz") */
  uri: string
  /** EIP-155 chain ID (e.g., 1 for mainnet) */
  chainId: number
  /** Random nonce to prevent replay attacks */
  nonce: string
  /** ISO 8601 datetime when the message was issued */
  issuedAt: string
  /** ISO 8601 datetime when the message expires (optional) */
  expirationTime?: string
  /** Resources the user is authorizing access to (optional) */
  resources?: string[]
}

/**
 * Parsed SIWE message
 */
export interface ParsedSIWEMessage extends SIWEMessageParams {
  /** SIWE version (always "1") */
  version: string
}

/**
 * SIWE verification result
 */
export interface SIWEVerificationResult {
  success: boolean
  address?: string
  error?: string
}

// ============================================================
// Security Validation
// ============================================================

/**
 * Validate domain against explicit allowlist
 * CRITICAL: Do NOT use window.location.host here
 */
export function validateDomain(domain: string): boolean {
  return ALLOWED_DOMAINS.includes(domain)
}

/**
 * Validate chain ID against explicit allowlist
 */
export function validateChainId(chainId: number): boolean {
  return ALLOWED_CHAIN_IDS.includes(chainId)
}

/**
 * Get current domain for SIWE message
 * Uses allowlist validation
 */
function getCurrentDomain(): string {
  if (typeof window === 'undefined') {
    return 'fairdrop.xyz'
  }

  const host = window.location.host
  if (validateDomain(host)) {
    return host
  }

  // Fallback to production domain if current host not in allowlist
  console.warn(`Domain ${host} not in allowlist, using fairdrop.xyz`)
  return 'fairdrop.xyz'
}

/**
 * Get current URI for SIWE message
 */
function getCurrentUri(): string {
  if (typeof window === 'undefined') {
    return 'https://fairdrop.xyz'
  }

  const domain = getCurrentDomain()
  const protocol = window.location.protocol
  return `${protocol}//${domain}`
}

// ============================================================
// Nonce Generation
// ============================================================

/**
 * Generate a cryptographically secure nonce locally
 */
function generateLocalNonce(): string {
  return crypto.randomUUID()
}

/**
 * Generate a nonce for SIWE
 * Tries fds-id server first, falls back to local generation
 */
export async function generateNonce(): Promise<string> {
  return withFdsIdFallback(
    // Server call
    async () => {
      const client = getFdsIdClient()
      const response = await client.getNonce()
      return response.nonce
    },
    // Local fallback
    () => {
      const nonce = generateLocalNonce()
      storeLocalNonce(nonce)
      notifyDegradedMode()
      return nonce
    }
  )
}

// ============================================================
// Nonce Verification
// ============================================================

/**
 * Verify and consume a nonce
 * Returns true if nonce is valid and not expired
 */
export function verifyNonce(nonce: string): boolean {
  return verifyAndConsumeNonce(nonce)
}

// ============================================================
// Message Creation
// ============================================================

/**
 * Create a SIWE message string following EIP-4361 format
 */
export function createSIWEMessage(params: SIWEMessageParams): string {
  const {
    domain,
    address,
    statement,
    uri,
    chainId,
    nonce,
    issuedAt,
    expirationTime,
    resources,
  } = params

  // Build message following EIP-4361 format
  let message = `${domain} wants you to sign in with your Ethereum account:\n`
  message += `${address}\n\n`

  if (statement) {
    message += `${statement}\n\n`
  }

  message += `URI: ${uri}\n`
  message += `Version: 1\n`
  message += `Chain ID: ${chainId}\n`
  message += `Nonce: ${nonce}\n`
  message += `Issued At: ${issuedAt}`

  if (expirationTime) {
    message += `\nExpiration Time: ${expirationTime}`
  }

  if (resources && resources.length > 0) {
    message += `\nResources:`
    resources.forEach((resource) => {
      message += `\n- ${resource}`
    })
  }

  return message
}

/**
 * Parse a SIWE message string back into its components
 */
export function parseSIWEMessage(message: string): ParsedSIWEMessage | null {
  try {
    const lines = message.split('\n')

    // First line: "{domain} wants you to sign in with your Ethereum account:"
    const domainMatch = lines[0]?.match(/^(.+) wants you to sign in with your Ethereum account:$/)
    if (!domainMatch) return null
    const domain = domainMatch[1]

    // Second line: address
    const address = lines[1]?.trim()
    if (!address || !address.match(/^0x[a-fA-F0-9]{40}$/)) return null

    // Parse remaining fields
    const fields: Record<string, string> = {}
    let statement = ''
    let resources: string[] = []
    let inResources = false

    for (let i = 2; i < lines.length; i++) {
      const line = lines[i]
      if (!line) continue

      if (line.startsWith('URI: ')) {
        fields['uri'] = line.slice(5)
      } else if (line.startsWith('Version: ')) {
        fields['version'] = line.slice(9)
      } else if (line.startsWith('Chain ID: ')) {
        fields['chainId'] = line.slice(10)
      } else if (line.startsWith('Nonce: ')) {
        fields['nonce'] = line.slice(7)
      } else if (line.startsWith('Issued At: ')) {
        fields['issuedAt'] = line.slice(11)
      } else if (line.startsWith('Expiration Time: ')) {
        fields['expirationTime'] = line.slice(17)
      } else if (line === 'Resources:') {
        inResources = true
      } else if (inResources && line.startsWith('- ')) {
        resources.push(line.slice(2))
      } else if (!fields['uri']) {
        // Must be part of statement (before URI line)
        if (line.trim()) {
          if (statement) statement += '\n'
          statement += line
        }
      }
    }

    return {
      domain,
      address,
      statement: statement || undefined,
      uri: fields['uri'] || '',
      version: fields['version'] || '1',
      chainId: parseInt(fields['chainId'] || '1', 10),
      nonce: fields['nonce'] || '',
      issuedAt: fields['issuedAt'] || '',
      expirationTime: fields['expirationTime'],
      resources: resources.length > 0 ? resources : undefined,
    }
  } catch {
    return null
  }
}

/**
 * Create a SIWE message for Fairdrop authentication
 */
export async function createFairdropSIWEMessage(
  address: string,
  chainId: number = 1
): Promise<{ message: string; nonce: string }> {
  // Validate chain ID
  if (!validateChainId(chainId)) {
    throw new Error(`Invalid chain ID: ${chainId}. Allowed: ${ALLOWED_CHAIN_IDS.join(', ')}`)
  }

  const nonce = await generateNonce()
  const domain = getCurrentDomain()
  const uri = getCurrentUri()

  // Calculate expiration (5 minutes from now)
  const issuedAt = new Date().toISOString()
  const expirationTime = new Date(Date.now() + NONCE_TTL_MS).toISOString()

  const message = createSIWEMessage({
    domain,
    address,
    statement: 'Sign in to Fairdrop. This request will not trigger a blockchain transaction or cost any gas fees.',
    uri,
    chainId,
    nonce,
    issuedAt,
    expirationTime,
  })

  return { message, nonce }
}

// ============================================================
// Signature Verification
// ============================================================

/**
 * Verify SIWE signature locally using ethers
 */
async function localVerifySIWESignature(
  message: string,
  signature: string,
  expectedAddress: string
): Promise<SIWEVerificationResult> {
  try {
    // Import ethers for signature verification
    const { verifyMessage } = await import('ethers')

    // Verify the signature
    const recoveredAddress = verifyMessage(message, signature)

    // Check if recovered address matches
    if (recoveredAddress.toLowerCase() !== expectedAddress.toLowerCase()) {
      return {
        success: false,
        error: 'Signature does not match the expected address',
      }
    }

    // Parse the message to verify nonce and validate fields
    const parsed = parseSIWEMessage(message)
    if (!parsed) {
      return {
        success: false,
        error: 'Invalid SIWE message format',
      }
    }

    // Validate domain (defense in depth)
    if (!validateDomain(parsed.domain)) {
      return {
        success: false,
        error: `Invalid domain: ${parsed.domain}`,
      }
    }

    // Validate chain ID
    if (!validateChainId(parsed.chainId)) {
      return {
        success: false,
        error: `Invalid chain ID: ${parsed.chainId}`,
      }
    }

    // Verify nonce (this also consumes it)
    if (!verifyNonce(parsed.nonce)) {
      return {
        success: false,
        error: 'Invalid or expired nonce',
      }
    }

    // Check expiration if present
    if (parsed.expirationTime) {
      const expiration = new Date(parsed.expirationTime)
      if (expiration < new Date()) {
        return {
          success: false,
          error: 'Message has expired',
        }
      }
    }

    return {
      success: true,
      address: recoveredAddress,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Verification failed',
    }
  }
}

/**
 * Verify a SIWE signature
 * Validates domain and chain ID first, then verifies signature
 * Tries fds-id server when available, falls back to local verification
 */
export async function verifySIWESignature(
  message: string,
  signature: string,
  expectedAddress: string
): Promise<SIWEVerificationResult> {
  // Parse and validate message FIRST (defense in depth)
  const parsed = parseSIWEMessage(message)
  if (!parsed) {
    return { success: false, error: 'Invalid SIWE message format' }
  }

  // Validate domain (always, regardless of server)
  if (!validateDomain(parsed.domain)) {
    return { success: false, error: `Invalid domain: ${parsed.domain}` }
  }

  // Validate chain ID
  if (!validateChainId(parsed.chainId)) {
    return { success: false, error: `Invalid chain ID: ${parsed.chainId}` }
  }

  // Try server verification with fallback to local
  return withFdsIdFallback(
    // Server call
    async () => {
      const client = getFdsIdClient()
      const result = await client.verifySIWE(message, signature)

      if (!result.success) {
        // Server verification failed - try local verification as fallback.
        // This handles server-side nonce/session issues while the signature
        // itself may be valid. Local verification is safe because it
        // independently validates the cryptographic signature.
        return localVerifySIWESignature(message, signature, expectedAddress)
      }

      // Double-check address matches (defense in depth)
      if (result.address?.toLowerCase() !== expectedAddress.toLowerCase()) {
        return {
          success: false,
          error: 'Signature does not match the expected address',
        }
      }

      return result
    },
    // Local fallback
    async () => {
      notifyDegradedMode()
      return localVerifySIWESignature(message, signature, expectedAddress)
    }
  )
}

// ============================================================
// Degradation Notification
// ============================================================

/**
 * Notify user that fds-id is unavailable (once per session)
 */
function notifyDegradedMode(): void {
  if (isDegradedModeNotified()) return

  // Only log in development - actual toast notification is in the UI hook
  if (import.meta.env.DEV) {
    console.info('[fds-id] Server unavailable, using local fallback')
  }

  markDegradedModeNotified()

  // Dispatch event for UI components to show toast
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('fairdrop:degraded-mode'))
  }
}

// ============================================================
// Exports for testing
// ============================================================

export const __testing = {
  ALLOWED_DOMAINS,
  ALLOWED_CHAIN_IDS,
  generateLocalNonce,
  localVerifySIWESignature,
  getCurrentDomain,
  getCurrentUri,
}
